// MITARBEITER-APP - Diese Datei gehört in: mitarbeiter-app/src/App.tsx
// 
// ========== SICHERHEITSÜBERSICHT ==========
// 1. XSS-SCHUTZ: React escapet automatisch alle Ausgaben
//    - Alle Eingaben (Name, Fahrzeug, Aufgaben) werden als Text behandelt
//    - JavaScript-Code in Eingabefeldern wird NICHT ausgeführt
// 
// 2. FOTO-UPLOAD SICHERHEIT:
//    - Nur JPEG, PNG, WebP erlaubt (KEIN SVG wegen möglichem JavaScript)
//    - Magic Number Validierung (prüft echte Dateiinhalte)
//    - Dateinamen-Sanitization (entfernt gefährliche Zeichen)
//    - Größenlimit: 10MB
//    - Bildgrößen-Limit: max 10000x10000 Pixel
//    - URL-Validierung bei Anzeige (nur HTTPS von Firebase)
//    - Path Traversal Schutz in Upload-Pfaden
// 
// 3. DATENVALIDIERUNG:
//    - Arbeitszeiten: 0-24 Stunden (0 für Urlaub/Krankheit erlaubt)
//    - Negative Werte werden blockiert
//    - Firebase Transactions für atomare Updates
// 
// 4. AUTHENTIFIZIERUNG:
//    - PIN-basierte Anmeldung über Firestore
//    - 24-Stunden persistente Authentifizierung
// ==========================================

import React, { useState, useEffect, } from 'react';
import PinLogin from './PinLogin';

// =================== FIREBASE INTEGRATION ===================
// SICHERHEIT: React escapet automatisch alle Ausgaben, daher sind XSS-Angriffe
// durch JavaScript-Code in Eingabefeldern (Name, Fahrzeug, Aufgaben) nicht möglich.
// Alle Benutzereingaben werden als Text behandelt, nicht als ausführbarer Code.

import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, onValue, runTransaction, update, get, push } from 'firebase/database';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCaUBolpNmTNucUG14nN7PKFijYL05ZpoY",
  authDomain: "werkstatt-app-561f9.firebaseapp.com",
  databaseURL: "https://werkstatt-app-561f9-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "werkstatt-app-561f9",
  storageBucket: "werkstatt-app-561f9.firebasestorage.app",
  messagingSenderId: "985738369659",
  appId: "1:985738369659:web:20c8ef37f6313092aba0de"
};

// Firebase initialisieren
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const firestore = getFirestore(app);
const storage = getStorage(app);

// Deep Comparison Helper
const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
};

// =================== AUTH HELPER MIT 24H SPEICHERUNG ===================
const AuthHelper = {
  AUTH_KEY: 'mitarbeiter_auth',
  AUTH_DURATION: 24 * 60 * 60 * 1000, // 24 Stunden in Millisekunden

  saveAuth: (user: string) => {
    const authData = {
      user: user,
      timestamp: Date.now(),
      expiresAt: Date.now() + AuthHelper.AUTH_DURATION
    };
    localStorage.setItem(AuthHelper.AUTH_KEY, JSON.stringify(authData));
  },

  getAuth: () => {
    try {
      const authStr = localStorage.getItem(AuthHelper.AUTH_KEY);
      if (!authStr) return null;

      const authData = JSON.parse(authStr);
      
      // Prüfe ob noch gültig
      if (Date.now() > authData.expiresAt) {
        AuthHelper.clearAuth();
        return null;
      }

      return authData.user;
    } catch {
      return null;
    }
  },

  clearAuth: () => {
    localStorage.removeItem(AuthHelper.AUTH_KEY);
  },

  getTimeRemaining: () => {
    try {
      const authStr = localStorage.getItem(AuthHelper.AUTH_KEY);
      if (!authStr) return 0;

      const authData = JSON.parse(authStr);
      const remaining = authData.expiresAt - Date.now();
      return remaining > 0 ? remaining : 0;
    } catch {
      return 0;
    }
  }
};

// Mitarbeiter Authentifizierung
async function authenticateMitarbeiter(name: string, pin: string): Promise<boolean> {
  try {
    const mitarbeiterDoc = await getDoc(doc(firestore, 'mitarbeiter', name));
    if (mitarbeiterDoc.exists()) {
      const data = mitarbeiterDoc.data();
      return data.pin === pin;
    }
    return false;
  } catch (error) {
    console.error('Auth Fehler:', error);
    return false;
  }
}

// Firebase Upload Funktion mit verbesserter Sicherheit
async function uploadPhotoToFirebase(file: File, orderId: string): Promise<string> {
  // SICHERHEIT: OrderId sanitizen (nur alphanumerisch + Unterstrich)
  const safeOrderId = orderId.replace(/[^a-zA-Z0-9_-]/g, '_');
  
  // SICHERHEIT: Sichere Pfad-Konstruktion ohne ../ oder andere gefährliche Zeichen
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const fileName = `orders/${safeOrderId}/photo_${timestamp}_${randomString}.jpg`;
  
  const photoRef = storageRef(storage, fileName);
  
  // Upload mit Metadaten für zusätzliche Sicherheit
  const metadata = {
    contentType: file.type,
    customMetadata: {
      uploadedBy: AuthHelper.getAuth() || 'unknown',
      uploadTime: new Date().toISOString(),
      originalName: file.name.substring(0, 100) // Begrenze Länge
    }
  };
  
  const snapshot = await uploadBytes(photoRef, file, metadata);
  const downloadURL = await getDownloadURL(snapshot.ref);
  
  return downloadURL;
}

// =================== SECURITY UTILS ===================

// KORRIGIERTER Storage Manager mit KRITISCHEN FIXES
const StorageManager = {
  KEYS: {
    ORDERS: 'orders/data',
    DAILY_HOURS: 'dailyHours/entries',
    MESSAGES: 'messages/list',
    METADATA: 'orders/metadata',
    BACKUPS: 'backups'
  },

  // KRITISCH: Backup vor JEDER Änderung
  createBackup: async (orderId: string, data: any, action: string): Promise<void> => {
    try {
      const backupRef = push(ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}`));
      await set(backupRef, {
        timestamp: new Date().toISOString(),
        action,
        data: JSON.parse(JSON.stringify(data)), // Deep copy
        user: AuthHelper.getAuth() || 'unknown'
      });
      
      // Behalte nur die letzten 50 Backups pro Auftrag
      const backupsRef = ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}`);
      const snapshot = await get(backupsRef);
      if (snapshot.exists()) {
        const backups = Object.entries(snapshot.val())
          .sort(([, a]: any, [, b]: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        if (backups.length > 50) {
          // Lösche alte Backups
          for (let i = 50; i < backups.length; i++) {
            await set(ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}/${backups[i][0]}`), null);
          }
        }
      }
    } catch (error) {
      console.error('Backup-Fehler:', error);
    }
  },

  // KRITISCH KORRIGIERT: Sichere Speicherung mit Validierung
  saveOrder: async (orderId: string, updates: any, currentUser: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      
      // WICHTIG: Hole ZUERST die aktuellen Daten für Backup
      const currentSnapshot = await get(orderRef);
      if (currentSnapshot.exists()) {
        await StorageManager.createBackup(orderId, currentSnapshot.val(), 'pre-update');
      }
      
      let success = false;
      let conflictDetected = false;
      
      await runTransaction(orderRef, (currentData) => {
        // Neuer Auftrag
        if (!currentData) {
          success = true;
          return {
            ...updates,
            id: orderId,
            createdAt: new Date().toISOString(),
            version: 1,
            lastModifiedBy: currentUser,
            changeHistory: [{
              id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              timestamp: new Date().toISOString(),
              user: currentUser,
              action: 'Erstellt',
              changes: updates
            }]
          };
        }
        
        // KRITISCH: Version Check
        if (updates._expectedVersion && currentData.version !== updates._expectedVersion) {
          conflictDetected = true;
          return; // Abort transaction
        }
        
        // Erstelle merged data
        let mergedData = { ...currentData };
        
        // KRITISCH: Arrays IMMER komplett ersetzen, nie mergen!
        if (updates.hasOwnProperty('timeEntries')) {
          // Validiere timeEntries
          if (Array.isArray(updates.timeEntries)) {
            mergedData.timeEntries = updates.timeEntries.map((entry: any) => ({
              ...entry,
              id: entry.id || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }));
          } else {
            console.error('timeEntries ist kein Array!');
            return;
          }
        }
        
        if (updates.hasOwnProperty('materials')) {
          if (Array.isArray(updates.materials)) {
            mergedData.materials = updates.materials;
          }
        }
        
        if (updates.hasOwnProperty('photos')) {
          if (Array.isArray(updates.photos)) {
            mergedData.photos = updates.photos;
          }
        }
        
        // Tasks mit Priority
        if (updates.hasOwnProperty('tasks')) {
          mergedData.tasks = updates.tasks;
        }
        
        if (updates.hasOwnProperty('tasksPriority')) {
          mergedData.tasksPriority = updates.tasksPriority;
        }
        
        // Andere Felder normal updaten
        Object.keys(updates).forEach(key => {
          if (!['timeEntries', 'materials', 'photos', 'tasks', '_expectedVersion'].includes(key)) {
            mergedData[key] = updates[key];
          }
        });
        
        // Metadaten updaten
        mergedData.version = (currentData.version || 0) + 1;
        mergedData.lastModified = new Date().toISOString();
        mergedData.lastModifiedBy = currentUser;
        
        // Change History mit eindeutiger ID
        const changeId = `${Date.now()}_${currentUser}_${Math.random().toString(36).substr(2, 9)}`;
        mergedData.changeHistory = [
          ...(currentData.changeHistory || []),
          {
            id: changeId,
            timestamp: new Date().toISOString(),
            user: currentUser,
            action: determineAction(updates),
            changes: updates,
            previousVersion: currentData.version || 0
          }
        ];
        
        success = true;
        return mergedData;
      });
      
      if (conflictDetected) {
        throw new Error('VERSION_CONFLICT');
      }
      
      if (success) {
        // Update Metadata
        await update(ref(database, StorageManager.KEYS.METADATA), {
          lastUpdate: new Date().toISOString(),
          lastUpdateBy: currentUser
        });
      }
      
      return success;
    } catch (error: any) {
      console.error('Speicherfehler:', error);
      if (error.message === 'VERSION_CONFLICT') {
        throw error;
      }
      return false;
    }
  },

  // Spezielle Methode für Zeiteinträge
  addTimeEntry: async (orderId: string, hours: number, employee: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      const snapshot = await get(orderRef);
      
      if (!snapshot.exists()) {
        console.error('Auftrag nicht gefunden');
        return false;
      }
      
      const currentOrder = snapshot.val();
      const newEntry = {
        id: `${Date.now()}_${employee}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        hours: hours,
        employee: employee
      };
      
      const updatedTimeEntries = [...(currentOrder.timeEntries || []), newEntry];
      
      return await StorageManager.saveOrder(
        orderId, 
        { 
          timeEntries: updatedTimeEntries,
          _expectedVersion: currentOrder.version 
        }, 
        employee
      );
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Zeit:', error);
      return false;
    }
  },

  // KORRIGIERT: Update von Zeiteinträgen
  updateTimeEntry: async (orderId: string, entryId: string, newHours: number, currentUser: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      const snapshot = await get(orderRef);
      
      if (!snapshot.exists()) return false;
      
      const currentOrder = snapshot.val();
      const updatedTimeEntries = (currentOrder.timeEntries || []).map((entry: any) => 
        entry.id === entryId 
          ? { ...entry, hours: newHours, lastModified: new Date().toISOString() } 
          : entry
      );
      
      return await StorageManager.saveOrder(
        orderId,
        {
          timeEntries: updatedTimeEntries,
          _expectedVersion: currentOrder.version
        },
        currentUser
      );
    } catch (error) {
      console.error('Fehler beim Update des Zeiteintrags:', error);
      return false;
    }
  },

  // Sichere Löschung von Zeiteinträgen
  deleteTimeEntry: async (orderId: string, entryId: string, currentUser: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      const snapshot = await get(orderRef);
      
      if (!snapshot.exists()) return false;
      
      const currentOrder = snapshot.val();
      const updatedTimeEntries = (currentOrder.timeEntries || []).filter((e: any) => e.id !== entryId);
      
      return await StorageManager.saveOrder(
        orderId,
        {
          timeEntries: updatedTimeEntries,
          _expectedVersion: currentOrder.version
        },
        currentUser
      );
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      return false;
    }
  },

  // VERBESSERTE saveDailyHours mit Transaction
  saveDailyHours: async (dailyHours: any[]): Promise<boolean> => {
    try {
      const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
      
      // Verwende Transaction für atomare Updates
      const result = await runTransaction(hoursRef, (currentData) => {
        console.log('Transaction: Aktuelle Daten:', currentData?.length || 0, 'Einträge');
        
        // Wenn keine Daten existieren, initialisiere mit den neuen Daten
        if (!currentData || !Array.isArray(currentData)) {
          console.log('Transaction: Keine existierenden Daten, setze neue Daten');
          return dailyHours;
        }
        
        // Konvertiere zu Map für effizientes Merging
        const currentMap = new Map();
        currentData.forEach(entry => {
          if (entry && entry.employee && entry.date) {
            const key = `${entry.employee}_${entry.date}`;
            currentMap.set(key, entry);
          }
        });
        
        // Merge neue Daten
        let hasChanges = false;
        dailyHours.forEach(newEntry => {
          if (newEntry && newEntry.employee && newEntry.date) {
            const key = `${newEntry.employee}_${newEntry.date}`;
            const existingEntry = currentMap.get(key);
            
            // Nur updaten wenn sich was geändert hat
            if (!existingEntry || existingEntry.hours !== newEntry.hours) {
              currentMap.set(key, {
                ...newEntry,
                updatedAt: new Date().toISOString()
              });
              hasChanges = true;
            }
          }
        });
        
        // Nur speichern wenn Änderungen vorhanden
        if (!hasChanges) {
          console.log('Transaction: Keine Änderungen gefunden, abort');
          return; // Abort transaction
        }
        
        // Konvertiere zurück zu Array
        const mergedData = Array.from(currentMap.values());
        console.log('Transaction: Speichere', mergedData.length, 'Einträge');
        
        return mergedData;
      });
      
      if (result.committed) {
        // Update Metadata
        await update(ref(database, 'dailyHours/metadata'), {
          lastUpdate: new Date().toISOString(),
          updatedBy: AuthHelper.getAuth() || 'unknown'
        });
        console.log('DailyHours erfolgreich gespeichert');
        return true;
      } else {
        console.log('Transaction abgebrochen - keine Änderungen');
        return true; // Kein Fehler, nur keine Änderungen
      }
    } catch (error) {
      console.error('Fehler beim Speichern der DailyHours:', error);
      return false;
    }
  },

  // NEU: Einzelner Eintrag Update (empfohlen!)
  upsertDailyHour: async (employee: string, date: string, hours: number): Promise<boolean> => {
    try {
      const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
      
      const result = await runTransaction(hoursRef, (currentData) => {
        // Initialisiere mit leerem Array wenn keine Daten
        if (!currentData) currentData = [];
        if (!Array.isArray(currentData)) {
          console.error('DailyHours ist kein Array!');
          return;
        }
        
        // Finde existierenden Eintrag
        const existingIndex = currentData.findIndex((entry: any) => 
          entry && entry.employee === employee && entry.date === date
        );
        
        if (existingIndex >= 0) {
          // Update existing
          console.log(`Update DailyHour: ${employee} - ${date} - ${hours}h`);
          currentData[existingIndex] = {
            ...currentData[existingIndex],
            hours,
            updatedAt: new Date().toISOString()
          };
        } else {
          // Add new
          console.log(`Neue DailyHour: ${employee} - ${date} - ${hours}h`);
          currentData.push({
            id: `${Date.now()}_${employee}_${Math.random().toString(36).substr(2, 9)}`,
            employee,
            date,
            hours,
            createdAt: new Date().toISOString()
          });
        }
        
        return currentData;
      });
      
      if (result.committed) {
        await update(ref(database, 'dailyHours/metadata'), {
          lastUpdate: new Date().toISOString(),
          updatedBy: employee
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Fehler beim Upsert der DailyHour:', error);
      return false;
    }
  },

  saveMessages: async (messages: any[]): Promise<boolean> => {
    try {
      await set(ref(database, StorageManager.KEYS.MESSAGES), messages);
      
      await update(ref(database, 'messages/metadata'), {
        lastUpdate: new Date().toISOString(),
        updatedBy: AuthHelper.getAuth() || 'unknown',
        count: messages.length
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern der Messages:', error);
      return false;
    }
  },

  loadAllOrders: async (): Promise<any[]> => {
    try {
      const ordersRef = ref(database, StorageManager.KEYS.ORDERS);
      const snapshot = await get(ordersRef);
      
      if (snapshot.exists()) {
        const ordersObj = snapshot.val();
        return Object.values(ordersObj);
      }
      return [];
    } catch (error) {
      console.error('Fehler beim Laden der Aufträge:', error);
      return [];
    }
  }
};

// Bestätigungs-Dialog Hook
const useConfirmDialog = () => {
  const confirm = (message: string, confirmText = 'LÖSCHEN') => {
    return new Promise<boolean>((resolve) => {
      const userInput = window.prompt(
        `${message}\n\nZur Bestätigung bitte "${confirmText}" eingeben:`
      );
      resolve(userInput === confirmText);
    });
  };

  const simpleConfirm = (message: string) => {
    return window.confirm(message);
  };

  return { confirm, simpleConfirm };
};

// Änderungsprotokoll
const determineAction = (changes: any) => {
  if (changes.deleted) return 'Gelöscht';
  if (changes.status) return `Status geändert zu ${changes.status}`;
  if (changes.assignedTo) return 'Zuweisung geändert';
  if (changes.timeEntries) return 'Arbeitszeit aktualisiert';
  if (changes.materials) return 'Material aktualisiert';
  if (changes.photos) return 'Foto hinzugefügt';
  if (changes.tasks) return 'Aufgaben aktualisiert';
  return 'Aktualisiert';
};

// Daten-Validierung
const DataValidator = {
  validateOrder: (order: any) => {
    const errors = [];
    
    if (!order.customerName?.trim()) {
      errors.push('Kundenname fehlt');
    }
    if (!order.vehicle?.trim()) {
      errors.push('Fahrzeug fehlt');
    }
    if (!order.assignedTo || order.assignedTo.length === 0) {
      errors.push('Keine Zuweisung vorhanden');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  validateInput: (field: string, value: string) => {
    const errors: any = {};
    
    switch(field) {
      case 'customerName':
        if (!value.trim()) errors.customerName = 'Kundenname erforderlich';
        if (value.length > 100) errors.customerName = 'Maximal 100 Zeichen';
        break;
        
      case 'vehicle':
        if (!value.trim()) errors.vehicle = 'Fahrzeug erforderlich';
        if (value.length > 100) errors.vehicle = 'Maximal 100 Zeichen';
        break;
        
      case 'hoursToAdd':
        const hours = parseFloat(value);
        if (isNaN(hours) || hours < 0) errors.hours = 'Ungültige Stundenzahl';
        if (hours > 24) errors.hours = 'Maximal 24 Stunden pro Eintrag';
        break;
    }
    
    return errors;
  }
};

// Offline-Indikator Hook
const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Sichere Bildkomprimierung mit zusätzlichen Sicherheitschecks
async function compressImage(file: File, maxDim = 1200, quality = 0.8): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    reader.onload = (event) => {
      const img = new Image();
      
      // SICHERHEIT: Verhindere fehlerhafte Bilder
      img.onerror = () => {
        reject(new Error('Ungültiges Bildformat'));
      };
      
      img.onload = () => {
        // SICHERHEIT: Begrenze maximale Bildgröße
        if (img.width > 10000 || img.height > 10000) {
          reject(new Error('Bild ist zu groß (max. 10000x10000 Pixel)'));
          return;
        }
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas-Kontext konnte nicht erstellt werden'));
          return;
        }
        
        let width = img.width;
        let height = img.height;
        
        // Berechne neue Dimensionen
        if (width > height && width > maxDim) {
          height *= maxDim / width;
          width = maxDim;
        } else if (height > maxDim) {
          width *= maxDim / height;
          height = maxDim;
        }
        
        // SICHERHEIT: Runde Dimensionen für konsistente Ausgabe
        width = Math.round(width);
        height = Math.round(height);
        
        canvas.width = width;
        canvas.height = height;
        
        // SICHERHEIT: Weißer Hintergrund für Transparenz
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
        
        // Zeichne Bild
        ctx.drawImage(img, 0, 0, width, height);
        
        // Konvertiere zu Blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Fehler beim Komprimieren'));
            return;
          }
          
          // Erstelle sichere Datei mit JPEG-Endung
          const safeFileName = `compressed_${Date.now()}.jpg`;
          resolve(new File([blob], safeFileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          }));
        }, 'image/jpeg', quality);
      };
      
      // SICHERHEIT: Setze Bild-Source in try-catch
      try {
        img.src = event.target?.result as string;
      } catch (error) {
        reject(new Error('Fehler beim Verarbeiten der Bilddaten'));
      }
    };
    
    reader.readAsDataURL(file);
  });
}

// =================== HAUPT-APP KOMPONENTEN ===================

// Checkbox Parser mit Prioritäten
function parseTasksWithCheckboxes(text: string, priority: string = 'medium') {
    if (!text) return [];
    
    const lines = text.split('\n');
    return lines.map((line, index) => {
        const checkboxMatch = line.match(/^(\s*)--([x]?)\s*(.*)$/);
        if (checkboxMatch) {
            return {
                id: `task-${index}`,
                type: 'checkbox',
                indent: checkboxMatch[1],
                checked: checkboxMatch[2] === 'x',
                text: checkboxMatch[3],
                originalLine: line,
                lineIndex: index,
                priority: priority
            };
        }
        return {
            id: `line-${index}`,
            type: 'text',
            text: line,
            originalLine: line,
            lineIndex: index,
            priority: priority
        };
    });
}

function updateTasksText(originalText: string, taskId: string, newChecked: boolean) {
    const lines = originalText.split('\n');
    const taskIndex = parseInt(taskId.split('-')[1]);
    
    if (lines[taskIndex]) {
        const line = lines[taskIndex];
        const checkboxMatch = line.match(/^(\s*)--([x]?)\s*(.*)$/);
        if (checkboxMatch) {
            const [, indent, , text] = checkboxMatch;
            lines[taskIndex] = `${indent}--${newChecked ? 'x' : ''} ${text}`;
        }
    }
    
    return lines.join('\n');
}

interface TaskRendererProps {
    tasks: any[];
    originalText: string;
    priority?: string;
    onTaskToggle: (taskId: string, checked: boolean) => void;
    onEditStart: () => void;
    showEditButton?: boolean;
}

function TaskRenderer({ tasks, originalText, priority = 'medium', onTaskToggle, onEditStart, showEditButton = true }: TaskRendererProps) {
    const getPriorityColor = (priority: string) => {
        switch(priority) {
            case 'high': return 'border-red-500 bg-red-50';
            case 'low': return 'border-gray-300 bg-gray-50';
            default: return 'border-yellow-500 bg-yellow-50';
        }
    };

    const getPriorityLabel = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴 Wichtig';
            case 'low': return '🟢 Niedrig';
            default: return '🟡 Mittel';
        }
    };

    if (!tasks || tasks.length === 0) {
        return (
            <div className={`p-4 rounded-lg border-2 ${getPriorityColor(priority)}`}>
                <p className="text-sm text-gray-500">Keine Aufgaben definiert</p>
                <p className="text-xs text-gray-400 mt-1">
                    Tippe "✏️ Aufgaben bearbeiten" um Checklisten zu erstellen
                </p>
                {showEditButton && (
                    <button 
                        onClick={onEditStart}
                        className="text-sm text-blue-600 mt-3 bg-blue-50 px-3 py-2 rounded-md w-full"
                    >
                        ✏️ Aufgaben hinzufügen
                    </button>
                )}
            </div>
        );
    }

    const completedTasks = tasks.filter(t => t.type === 'checkbox' && t.checked).length;
    const totalTasks = tasks.filter(t => t.type === 'checkbox').length;

    return (
        <div className={`p-4 rounded-lg border-2 ${getPriorityColor(priority)}`}>
            <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-600">
                    Priorität: {getPriorityLabel(priority)}
                </span>
            </div>

            {totalTasks > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span className="font-medium">Fortschritt: {completedTasks}/{totalTasks}</span>
                        <span className="text-green-600 font-bold">
                            {Math.round((completedTasks / totalTasks) * 100)}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
                        />
                    </div>
                </div>
            )}
            
            <div className="space-y-3">
                {tasks.map(task => (
                    <div key={task.id} className="flex items-start gap-3">
                        {task.type === 'checkbox' ? (
                            <>
                                <input
                                    type="checkbox"
                                    checked={task.checked}
                                    onChange={(e) => onTaskToggle(task.id, e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className={`text-sm flex-1 leading-relaxed ${
                                    task.checked 
                                        ? 'line-through text-gray-500' 
                                        : 'text-gray-700 font-medium'
                                }`}>
                                    {task.text}
                                </span>
                                {task.checked && <span className="text-green-500 text-lg">✅</span>}
                            </>
                        ) : (
                            <span className="text-sm text-gray-700 ml-7 leading-relaxed">{task.text}</span>
                        )}
                    </div>
                ))}
            </div>
            
            {showEditButton && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                    <button 
                        onClick={onEditStart}
                        className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-md w-full"
                    >
                        ✏️ Aufgaben bearbeiten
                    </button>
                </div>
            )}
        </div>
    );
}

// =================== KALENDER KOMPONENTE MIT FEIERTAGEN ===================

// Österreichische Feiertage berechnen
function getAustrianHolidays(year: number): { [date: string]: string } {
    const holidays: { [date: string]: string } = {};
    
    // Feste Feiertage
    holidays[`${year}-01-01`] = 'Neujahr';
    holidays[`${year}-01-06`] = 'Heilige Drei Könige';
    holidays[`${year}-05-01`] = 'Staatsfeiertag';
    holidays[`${year}-08-15`] = 'Mariä Himmelfahrt';
    holidays[`${year}-10-26`] = 'Nationalfeiertag';
    holidays[`${year}-11-01`] = 'Allerheiligen';
    holidays[`${year}-12-08`] = 'Mariä Empfängnis';
    holidays[`${year}-12-25`] = 'Christtag';
    holidays[`${year}-12-26`] = 'Stefanitag';
    
    // Bewegliche Feiertage (Ostern-abhängig)
    const easter = calculateEaster(year);
    const easterDate = new Date(year, easter.month - 1, easter.day);
    
    // Debug-Log für Ostern
    console.log(`Ostern ${year}: ${formatDate(easterDate)}`);
    
    // Ostersonntag
    holidays[formatDate(easterDate)] = 'Ostersonntag';
    
    // Ostermontag (1 Tag nach Ostern)
    const easterMonday = new Date(easterDate);
    easterMonday.setDate(easterDate.getDate() + 1);
    holidays[formatDate(easterMonday)] = 'Ostermontag';
    
    // Christi Himmelfahrt (39 Tage nach Ostern)
    const ascension = new Date(easterDate);
    ascension.setDate(easterDate.getDate() + 39);
    holidays[formatDate(ascension)] = 'Christi Himmelfahrt';
    
    // Pfingstsonntag (49 Tage nach Ostern)
    const whitsun = new Date(easterDate);
    whitsun.setDate(easterDate.getDate() + 49);
    holidays[formatDate(whitsun)] = 'Pfingstsonntag';
    
    // Pfingstmontag (50 Tage nach Ostern)
    const whitMonday = new Date(easterDate);
    whitMonday.setDate(easterDate.getDate() + 50);
    holidays[formatDate(whitMonday)] = 'Pfingstmontag';
    console.log(`Pfingstmontag ${year}: ${formatDate(whitMonday)}`);
    
    // Fronleichnam (60 Tage nach Ostern - immer ein Donnerstag)
    const corpusChristi = new Date(easterDate);
    corpusChristi.setDate(easterDate.getDate() + 60);
    holidays[formatDate(corpusChristi)] = 'Fronleichnam';
    console.log(`Fronleichnam ${year}: ${formatDate(corpusChristi)}`);
    
    return holidays;
}

// Gaußsche Osterformel
function calculateEaster(year: number): { month: number; day: number } {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    return { month, day };
}

function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

interface CalendarViewProps {
    employee: string;
    dailyHours: any[];
    onDateClick: (date: string) => void;
    onClose: () => void;
}

function CalendarView({ employee, dailyHours, onDateClick, onClose }: CalendarViewProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startingDayOfWeek = monthStart.getDay();
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const holidays = getAustrianHolidays(currentMonth.getFullYear());
    const today = formatDate(new Date());
    
    // Berechne Kalender-Tage
    const calendarDays = [];
    for (let i = 0; i < adjustedStart; i++) {
        calendarDays.push(null);
    }
    
    for (let day = 1; day <= monthEnd.getDate(); day++) {
        const dateStr = formatDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
        const entry = dailyHours.find(e => e.employee === employee && e.date === dateStr);
        const dayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).getDay();
        
        calendarDays.push({
            date: dateStr,
            day: day,
            hours: entry ? entry.hours : null, // Zeige auch 0 Stunden an
            isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
            isHoliday: !!holidays[dateStr],
            holidayName: holidays[dateStr] || null,
            isToday: dateStr === today,
            isPast: new Date(dateStr) < new Date(today)
        });
    }
    
    // Statistiken berechnen
    const monthHours = dailyHours
        .filter(e => e.employee === employee && e.date.startsWith(formatDate(monthStart).substring(0, 7)))
        .reduce((sum, e) => sum + (e.hours || 0), 0);
    
    const workDaysCount = calendarDays.filter(d => d && !d.isWeekend && !d.isHoliday && d.isPast).length;
    const avgHours = workDaysCount > 0 ? (monthHours / workDaysCount).toFixed(1) : '0';
    
    const navigateMonth = (direction: number) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">📅 Kalenderansicht - {employee}</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 btn-mobile">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Monatsnavigation */}
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => navigateMonth(-1)}
                            className="p-2 hover:bg-gray-100 rounded btn-mobile"
                        >
                            ‹
                        </button>
                        <h3 className="text-xl font-medium">
                            {currentMonth.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button
                            onClick={() => navigateMonth(1)}
                            className="p-2 hover:bg-gray-100 rounded btn-mobile"
                        >
                            ›
                        </button>
                    </div>
                    
                    {/* Statistiken */}
                    <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">{monthHours.toFixed(1)}h</p>
                            <p className="text-sm text-gray-600">Gesamtstunden</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">{avgHours}h</p>
                            <p className="text-sm text-gray-600">Ø pro Tag</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-purple-600">{workDaysCount}</p>
                            <p className="text-sm text-gray-600">Arbeitstage</p>
                        </div>
                    </div>
                    
                    {/* Kalender */}
                    <div className="grid grid-cols-7 gap-1">
                        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                            <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                                {day}
                            </div>
                        ))}
                        
                        {calendarDays.map((day, index) => 
                            day === null ? (
                                <div key={`empty-${index}`} />
                            ) : (
                                <div
                                    key={day.date}
                                    onClick={() => {
                                        setSelectedDate(day.date);
                                        onDateClick(day.date);
                                    }}
                                    className={`
                                        p-2 text-center rounded-md text-sm btn-mobile cursor-pointer
                                        relative transition-all hover:shadow-md
                                        ${day.isToday ? 'ring-2 ring-blue-500' : ''}
                                        ${day.hours !== null && day.hours !== undefined ? 'bg-green-100 hover:bg-green-200' : 
                                          day.isHoliday ? 'bg-orange-100 hover:bg-orange-200' :
                                          day.isWeekend ? 'bg-gray-100 hover:bg-gray-200' :
                                          day.isPast ? 'bg-red-50 hover:bg-red-100' : 
                                          'bg-white hover:bg-gray-50'}
                                    `}
                                    title={day.holidayName || ''}
                                >
                                    <div className="font-medium">{day.day}</div>
                                    {day.hours !== null && day.hours !== undefined && (
                                        <div className="text-xs font-bold text-green-700">{day.hours}h</div>
                                    )}
                                    {day.isHoliday && (
                                        <div className="absolute top-1 right-1 text-xs">🎊</div>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                    
                    {/* Legende */}
                    <div className="mt-6 text-xs text-gray-600">
                        <p className="font-medium mb-2">Legende:</p>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 bg-green-100 rounded"></span>
                                <span>Arbeitszeit eingetragen</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 bg-red-50 rounded"></span>
                                <span>Keine Arbeitszeit (vergangen)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 bg-orange-100 rounded"></span>
                                <span>Feiertag 🎊</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 bg-gray-100 rounded"></span>
                                <span>Wochenende</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 bg-white border-2 border-blue-500 rounded"></span>
                                <span>Heute</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Feiertage des Monats */}
                    {Object.entries(holidays)
                        .filter(([date]) => date.startsWith(formatDate(monthStart).substring(0, 7)))
                        .length > 0 && (
                        <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                            <p className="font-medium text-sm mb-2">🎊 Feiertage in diesem Monat:</p>
                            <div className="space-y-1">
                                {Object.entries(holidays)
                                    .filter(([date]) => date.startsWith(formatDate(monthStart).substring(0, 7)))
                                    .map(([date, name]) => (
                                        <p key={date} className="text-xs text-gray-700">
                                            {new Date(date).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })} - {name}
                                        </p>
                                    ))
                                }
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const MITARBEITER = ['Darek', 'Erhan'];

export default function MitarbeiterApp() {
    // ========== 1. STATE HOOKS ==========
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentEmployee, setCurrentEmployee] = useState('');
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState<any[]>([]);
    const [dailyHours, setDailyHours] = useState<any[]>([]);
    const [messages, setMessages] = useState<any[]>([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [viewState, setViewState] = useState('list');
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [showDailyHoursModal, setShowDailyHoursModal] = useState(false);
    const [showMessagesModal, setShowMessagesModal] = useState(false);
    const [showCalendarView, setShowCalendarView] = useState(false);
    const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
    const [quickCreateForm, setQuickCreateForm] = useState({
        customerName: '',
        vehicle: '',
        tasks: ''
    });
    const [formState, setFormState] = useState({
        customerName: '',
        vehicle: '',
        tasks: '',
        tasksPriority: 'medium',
        assignedTo: [] as string[],
        hoursToAdd: '',
        newMaterials: [{ quantity: '', name: '' }]
    });
    const [editingTimeEntryId, setEditingTimeEntryId] = useState<string | null>(null);
    const [editingTimeEntryHours, setEditingTimeEntryHours] = useState(0);
    const [validationErrors, setValidationErrors] = useState<any>({});
    
    // ========== 2. CUSTOM HOOKS ==========
    const { simpleConfirm } = useConfirmDialog();
    const isOnline = useOnlineStatus();

    // ========== 3. COMPUTED VALUES (MÜSSEN HIER SEIN!) ==========
    const myMessages = messages.filter(msg => 
        msg.to.includes(currentEmployee) || msg.to.includes('Alle')
    );
    const unreadMessages = myMessages.filter(msg => !msg.read);
    const unreadCount = unreadMessages.length;
    
    const myOrders = orders.filter(order => 
        order.assignedTo && 
        order.assignedTo.includes(currentEmployee) &&
        !order.deleted
    );
    
    const currentSelectedOrder = myOrders.find(o => o.id === selectedOrderId);
    const activeOrders = myOrders.filter(o => 
        o.status === 'Neu' || o.status === 'In Arbeit' || o.status === 'draft'
    );
    const completedOrders = myOrders.filter(o => o.status === 'Erledigt');

    const today = new Date().toISOString().split('T')[0];
    const todayHoursEntered = dailyHours.some(entry => 
        entry.employee === currentEmployee && entry.date === today
    );
    
    const now = new Date();
    const dayOfWeek = now.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const holidays = getAustrianHolidays(now.getFullYear());
    const isHoliday = !!holidays[today];
    const isWorkday = !isWeekend && !isHoliday;
    
    const isLateReminder = isWorkday && now.getHours() >= 17 && now.getMinutes() >= 30 && !todayHoursEntered;
    
    // Prüfe ob noch Arbeitstage fehlen
    const checkMissingWorkdays = () => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        let missingCount = 0;
        const checkDate = new Date(thirtyDaysAgo);
        const todayDate = new Date();
        
        while (checkDate < todayDate) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const dayOfWeek = checkDate.getDay();
            const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
            const yearHolidays = getAustrianHolidays(checkDate.getFullYear());
            const isHolidayDay = !!yearHolidays[dateStr];
            
            if (!isWeekendDay && !isHolidayDay) {
                const hasEntry = dailyHours.some(entry => 
                    entry.employee === currentEmployee && 
                    entry.date === dateStr &&
                    entry.hours !== null && 
                    entry.hours !== undefined
                );
                if (!hasEntry && dateStr !== today) {
                    missingCount++;
                }
            }
            
            checkDate.setDate(checkDate.getDate() + 1);
        }
        
        return missingCount;
    };
    
    const missingWorkdays = checkMissingWorkdays();

    // ========== 4. EFFECTS ==========
    useEffect(() => {
        const savedUser = AuthHelper.getAuth();
        if (savedUser) {
            setIsAuthenticated(true);
            setCurrentEmployee(savedUser);
        }
        setLoading(false);
    }, []);

    // ✅ KRITISCH KORRIGIERTE Firebase Listener
    useEffect(() => {
        if (!isAuthenticated) return;

        let mounted = true;

        // Orders Listener mit Validierung
        const ordersRef = ref(database, StorageManager.KEYS.ORDERS);
        const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
            if (!mounted) return;
            
            console.log('Firebase Orders Update empfangen:', new Date().toISOString());
            
            if (snapshot.exists()) {
                const ordersObj = snapshot.val();
                const ordersArray = Object.values(ordersObj).map((order: any) => {
                    // Validiere und bereinige Order-Daten
                    return {
                        ...order,
                        id: order.id || 'unknown',
                        timeEntries: Array.isArray(order.timeEntries) ? order.timeEntries : [],
                        materials: Array.isArray(order.materials) ? order.materials : [],
                        photos: Array.isArray(order.photos) ? order.photos : [],
                        assignedTo: Array.isArray(order.assignedTo) ? order.assignedTo : [],
                        tasksPriority: order.tasksPriority || 'medium'
                    };
                });
                console.log('Anzahl Orders von Firebase:', ordersArray.length);
                setOrders(ordersArray);
            } else {
                console.log('Keine Orders in Firebase gefunden');
                setOrders([]);
            }
            setDataLoaded(true);
        }, (error) => {
            console.error('Firebase Orders Fehler:', error);
            alert('Fehler beim Laden der Aufträge. Bitte Seite neu laden.');
            setDataLoaded(true);
        });

        // ✅ KRITISCH KORRIGIERTER Daily Hours Listener
        const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
        const unsubscribeHours = onValue(hoursRef, (snapshot) => {
            if (!mounted) return;
            
            console.log('Firebase DailyHours Update empfangen:', new Date().toISOString());
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                
                if (Array.isArray(data) && data.length > 0) {
                    console.log(`DailyHours geladen: ${data.length} Einträge`);
                    
                    // Validiere jeden Eintrag
                    const validEntries = data.filter((entry: any) => 
                        entry && 
                        entry.employee && 
                        entry.date && 
                        typeof entry.hours === 'number' && 
                        entry.hours >= 0 && 
                        entry.hours <= 24 &&
                        entry.id // ID muss vorhanden sein
                    );
                    
                    if (validEntries.length > 0) {
                        setDailyHours(prevHours => {
                            // Nur updaten wenn sich wirklich was geändert hat
                            if (deepEqual(prevHours, validEntries)) {
                                console.log('DailyHours unverändert');
                                return prevHours;
                            }
                            
                            console.log(`DailyHours Update: ${validEntries.length} gültige Einträge`);
                            return validEntries;
                        });
                    } else {
                        console.warn('Keine gültigen DailyHours Einträge gefunden - behalte lokalen State');
                        // WICHTIG: NICHT auf leeres Array setzen!
                    }
                } else if (Array.isArray(data) && data.length === 0) {
                    // Firebase hat explizit ein leeres Array
                    console.log('DailyHours ist leer in Firebase');
                    // Nur setzen wenn lokaler State auch leer ist
                    setDailyHours(prev => {
                        if (prev.length === 0) {
                            return []; // Bereits leer, OK
                        } else {
                            console.warn('Firebase hat leeres Array aber lokaler State hat Daten - behalte lokalen State!');
                            return prev; // Behalte lokale Daten!
                        }
                    });
                } else {
                    console.warn('DailyHours hat unerwartetes Format:', typeof data);
                    // NICHTS machen!
                }
            } else {
                // KRITISCH: Firebase hat keine Daten (null/undefined)
                console.log('Keine DailyHours in Firebase - behalte lokalen State');
                // NIEMALS auf leeres Array setzen!
                // setDailyHours([]); // ❌ NIEMALS!
            }
        }, (error) => {
            console.error('Firebase DailyHours Fehler:', error);
            // Bei Fehler nichts ändern!
        });

        // Messages Listener
        const messagesRef = ref(database, StorageManager.KEYS.MESSAGES);
        const unsubscribeMessages = onValue(messagesRef, (snapshot) => {
            if (!mounted) return;
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (Array.isArray(data)) {
                    setMessages(data);
                } else {
                    console.warn('Messages hat unerwartetes Format');
                }
            } else {
                console.log('Keine Messages in Firebase gefunden');
                // Nicht auf leeres Array setzen!
            }
        }, (error) => {
            console.error('Firebase Messages Fehler:', error);
        });

        return () => {
            mounted = false;
            unsubscribeOrders();
            unsubscribeHours();
            unsubscribeMessages();
        };
    }, [isAuthenticated]);

    // Auto-Save für Entwürfe
    useEffect(() => {
        if (currentSelectedOrder && currentSelectedOrder.status === 'draft') {
            const draftKey = `draft_${currentEmployee}_${currentSelectedOrder.id}`;
            localStorage.setItem(draftKey, JSON.stringify({
                ...formState,
                lastSaved: new Date().toISOString()
            }));
        }
    }, [currentSelectedOrder, formState, currentEmployee]);

    // Lade gespeicherten Entwurf
    useEffect(() => {
        if (currentSelectedOrder && currentSelectedOrder.status === 'draft') {
            const draftKey = `draft_${currentEmployee}_${currentSelectedOrder.id}`;
            const savedDraft = localStorage.getItem(draftKey);
            if (savedDraft) {
                const draft = JSON.parse(savedDraft);
                setFormState(draft);
            }
        }
    }, [currentSelectedOrder, currentEmployee]);

    // BUG FIX: Form State richtig initialisieren beim Auswählen eines Auftrags
    useEffect(() => {
        if (currentSelectedOrder) {
            setFormState({
                customerName: currentSelectedOrder.customerName || '',
                vehicle: currentSelectedOrder.vehicle || '',
                tasks: currentSelectedOrder.tasks || '',
                tasksPriority: currentSelectedOrder.tasksPriority || 'medium',
                assignedTo: currentSelectedOrder.assignedTo || [],
                hoursToAdd: '',
                newMaterials: [{ quantity: '', name: '' }]
            });
        }
    }, [currentSelectedOrder]);
    
    // ========== 5. EVENT HANDLERS ==========
    const handleLogout = () => {
        AuthHelper.clearAuth();
        setIsAuthenticated(false);
        setCurrentEmployee('');
    };

    const handleFormChange = (field: string, value: any) => {
        setFormState(prev => ({ ...prev, [field]: value }));
        
        // Live-Validierung
        const errors = DataValidator.validateInput(field, value);
        setValidationErrors((prev: any) => ({ ...prev, ...errors }));
    };

    const handleAssignmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        let assigned = [...formState.assignedTo];
        if (checked) {
            assigned.push(value);
        } else {
            assigned = assigned.filter(emp => emp !== value);
        }
        setFormState(prev => ({ ...prev, assignedTo: assigned }));
    };

    // Schnellerfassung - Als offen speichern
    const handleQuickCreateOpen = async () => {
        if (!quickCreateForm.customerName.trim()) {
            alert('Bitte Kundennamen eingeben');
            return;
        }
        
        const newOrderId = `${Date.now()}_${currentEmployee}_${Math.random().toString(36).substr(2, 9)}`;
        const newOrder = {
            status: 'Neu', 
            createdAt: new Date().toISOString(), 
            assignedTo: [currentEmployee],
            customerName: quickCreateForm.customerName, 
            vehicle: quickCreateForm.vehicle || '', 
            tasks: quickCreateForm.tasks || '',
            tasksPriority: 'medium',
            materials: [], 
            timeEntries: [], 
            photos: [],
            deleted: false,
            statusHistory: [{ status: 'Neu', timestamp: new Date().toISOString(), by: currentEmployee }]
        };
        
        const success = await StorageManager.saveOrder(newOrderId, newOrder, currentEmployee);
        
        if (success) {
            setQuickCreateForm({ customerName: '', vehicle: '', tasks: '' });
            alert('Auftrag erfolgreich erstellt!');
        } else {
            alert('Fehler beim Erstellen des Auftrags');
        }
    };
    
    // Schnellerfassung - Als erledigt speichern
    const handleQuickCreateCompleted = async () => {
        if (!quickCreateForm.customerName.trim()) {
            alert('Bitte Kundennamen eingeben');
            return;
        }
        
        const newOrderId = `${Date.now()}_${currentEmployee}_${Math.random().toString(36).substr(2, 9)}`;
        const newOrder = {
            status: 'Erledigt', 
            createdAt: new Date().toISOString(), 
            assignedTo: [currentEmployee],
            customerName: quickCreateForm.customerName, 
            vehicle: quickCreateForm.vehicle || '', 
            tasks: quickCreateForm.tasks || '',
            tasksPriority: 'medium',
            materials: [], 
            timeEntries: [], 
            photos: [],
            deleted: false,
            statusHistory: [
                { status: 'Neu', timestamp: new Date().toISOString(), by: currentEmployee },
                { status: 'Erledigt', timestamp: new Date().toISOString(), by: currentEmployee }
            ]
        };
        
        const success = await StorageManager.saveOrder(newOrderId, newOrder, currentEmployee);
        
        if (success) {
            setQuickCreateForm({ customerName: '', vehicle: '', tasks: '' });
            alert('Auftrag wurde als erledigt gespeichert!');
        } else {
            alert('Fehler beim Erstellen des Auftrags');
        }
    };

    // Neuen Auftrag erstellen mit eindeutiger ID
    const handleCreateNewOrder = async () => {
        const newOrderId = `${Date.now()}_${currentEmployee}_${Math.random().toString(36).substr(2, 9)}`;
        const newOrder = {
            status: 'draft', 
            createdAt: new Date().toISOString(), 
            assignedTo: [currentEmployee],
            customerName: '', 
            vehicle: '', 
            tasks: '',
            tasksPriority: 'medium',
            materials: [], 
            timeEntries: [], 
            photos: [],
            deleted: false,
            statusHistory: [{ status: 'Entwurf erstellt', timestamp: new Date().toISOString(), by: currentEmployee }]
        };
        
        const success = await StorageManager.saveOrder(newOrderId, newOrder, currentEmployee);
        
        if (success) {
            setSelectedOrderId(newOrderId);
            setViewState('details');
        } else {
            alert('Fehler beim Erstellen des Auftrags');
        }
    };
    
    // Order Details speichern mit Versionskontrolle
    const saveOrderDetails = async (updateData: any) => {
        if (!currentSelectedOrder) return;
        
        const payload = { 
            ...updateData,
            customerName: formState.customerName,  // IMMER aus formState nehmen
            vehicle: formState.vehicle,            // IMMER aus formState nehmen
            tasks: formState.tasks,                // IMMER aus formState nehmen
            tasksPriority: formState.tasksPriority,
            assignedTo: formState.assignedTo,
            _expectedVersion: currentSelectedOrder.version 
        };

        // Nur bei Draft den Status auf 'Neu' setzen
        if (currentSelectedOrder.status === 'draft') {
            payload.status = 'Neu';
        }
        
        const success = await StorageManager.saveOrder(
            currentSelectedOrder.id,
            payload,
            currentEmployee
        );
        
        if (success && currentSelectedOrder.status === 'draft') {
            const draftKey = `draft_${currentEmployee}_${currentSelectedOrder.id}`;
            localStorage.removeItem(draftKey);
        }
        
        return success;
    };

    const handleSaveAndExit = async () => {
        if (!currentSelectedOrder) return;
        
        // Validierung nur für neue Aufträge
        if (currentSelectedOrder.status === 'draft') {
            const validation = DataValidator.validateOrder({
                ...currentSelectedOrder,
                ...formState
            });
            
            if (!validation.isValid) {
                alert(`Bitte korrigieren Sie folgende Fehler:\n${validation.errors.join('\n')}`);
                return;
            }
        }
        
        const historyEntry = { status: 'gespeichert', timestamp: new Date().toISOString(), by: currentEmployee };
        const success = await saveOrderDetails({ 
            status: currentSelectedOrder.status === 'draft' ? 'In Arbeit' : currentSelectedOrder.status,
            statusHistory: [...(currentSelectedOrder.statusHistory || []), historyEntry] 
        });
        
        if (success) {
            setSelectedOrderId(null);
            setViewState('list');
        } else {
            if (await simpleConfirm('Die Daten wurden zwischenzeitlich geändert. Seite neu laden?')) {
                window.location.reload();
            }
        }
    };

    const handleAddTimeEntry = async () => {
        const hoursToAdd = parseFloat(formState.hoursToAdd);
        
        // Validierung
        if (hoursToAdd === undefined || hoursToAdd === null || hoursToAdd < 0) {
            alert('Bitte gültige Stundenzahl eingeben (0-24)');
            return;
        }
        if (hoursToAdd > 24) {
            alert('Maximal 24 Stunden pro Eintrag erlaubt');
            return;
        }
        
        const success = await StorageManager.addTimeEntry(
            currentSelectedOrder.id,
            hoursToAdd,
            currentEmployee
        );
        
        if (success) {
            setFormState(prev => ({...prev, hoursToAdd: ''}));
        } else {
            alert('Fehler beim Speichern. Bitte erneut versuchen.');
        }
    };
    
    const handleAddMaterials = async () => {
        const newMaterialsToAdd = formState.newMaterials.filter(m => m.name.trim() !== '');
        if (newMaterialsToAdd.length === 0) return;
        
        const materialsWithId = newMaterialsToAdd.map(m => ({
            ...m,
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }));
        
        const updatedMaterials = [...(currentSelectedOrder.materials || []), ...materialsWithId];
        
        const success = await StorageManager.saveOrder(
            currentSelectedOrder.id,
            { 
                materials: updatedMaterials,
                _expectedVersion: currentSelectedOrder.version 
            },
            currentEmployee
        );
        
        if (success) {
            setFormState(prev => ({...prev, newMaterials: [{quantity: '', name: ''}]}));
        }
    };
    
    const handleDeleteTimeEntry = async (entryId: string) => {
        if (!currentSelectedOrder) return;
        
        const entry = currentSelectedOrder.timeEntries.find((e: any) => e.id === entryId);
        if (!entry) return;
        
        // Nur eigene Einträge löschen
        if (entry.employee !== currentEmployee) {
            alert('Sie können nur Ihre eigenen Zeiteinträge löschen!');
            return;
        }
        
        // Doppelte Bestätigung für alte Einträge
        const entryAge = Date.now() - new Date(entry.timestamp).getTime();
        const isOld = entryAge > 24 * 60 * 60 * 1000; // 24 Stunden
        
        const message = isOld 
            ? `Dieser Zeiteintrag ist älter als 24 Stunden (${entry.hours}h). Wirklich löschen?`
            : `Zeiteintrag (${entry.hours}h) wirklich löschen?`;
        
        if (!await simpleConfirm(message)) return;
        
        if (isOld && !await simpleConfirm('Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden!')) {
            return;
        }
        
        const success = await StorageManager.deleteTimeEntry(
            currentSelectedOrder.id,
            entryId,
            currentEmployee
        );
        
        if (!success) {
            alert('Fehler beim Löschen');
        }
    };
    
    // KORRIGIERT: Zeiteintrag Update
    const handleSaveTimeEntryUpdate = async (entryId: string) => {
        if (!currentSelectedOrder) return;
        
        const updatedHours = parseFloat(editingTimeEntryHours.toString());
        if (isNaN(updatedHours) || updatedHours < 0 || updatedHours > 24) {
            alert('Ungültige Stundenzahl (0-24)');
            return;
        }
        
        const success = await StorageManager.updateTimeEntry(
            currentSelectedOrder.id,
            entryId,
            updatedHours,
            currentEmployee
        );
        
        if (success) {
            setEditingTimeEntryId(null);
            // Die Aktualisierung erfolgt automatisch durch den Firebase Listener
        } else {
            alert('Fehler beim Speichern der Änderung. Bitte erneut versuchen.');
        }
    };

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !currentSelectedOrder) return;

        // SICHERHEIT: Dateiname sanitizen (entferne gefährliche Zeichen)
        const sanitizedFileName = file.name
            .replace(/[^a-zA-Z0-9._-]/g, '_')
            .replace(/\.+/g, '.')
            .toLowerCase();

        // SICHERHEIT: Größenbeschränkung: 10MB (reduziert von 15MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('Foto ist zu groß! Maximal 10MB erlaubt.');
            return;
        }

        // SICHERHEIT: Nur sichere Bildformate erlauben (KEIN SVG wegen möglichem JavaScript!)
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
        
        // Prüfe MIME-Type
        if (!allowedTypes.includes(file.type)) {
            alert('Nur JPEG, PNG oder WebP Bilder sind erlaubt!');
            return;
        }

        // SICHERHEIT: Zusätzliche Dateiendungs-Prüfung
        const fileExtension = sanitizedFileName.substring(sanitizedFileName.lastIndexOf('.')).toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Ungültige Dateiendung! Nur .jpg, .jpeg, .png oder .webp erlaubt.');
            return;
        }

        // SICHERHEIT: Prüfe Magic Numbers (erste Bytes der Datei)
        const arrayBuffer = await file.slice(0, 4).arrayBuffer();
        const header = new Uint8Array(arrayBuffer);
        let validMagicNumber = false;

        // JPEG: FF D8 FF
        if (header[0] === 0xFF && header[1] === 0xD8 && header[2] === 0xFF) validMagicNumber = true;
        // PNG: 89 50 4E 47
        if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4E && header[3] === 0x47) validMagicNumber = true;
        // WebP: 52 49 46 46 (RIFF)
        if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) validMagicNumber = true;

        if (!validMagicNumber) {
            alert('Die Datei scheint kein gültiges Bild zu sein!');
            return;
        }

        try {
            // Erstelle sicheren Dateinamen für Upload
            const timestamp = Date.now();
            const safeFileName = `photo_${timestamp}_${sanitizedFileName}`;
            
            // Komprimiere das Bild
            const compressedFile = await compressImage(file);
            
            // Erstelle neue File mit sicherem Namen
            const safeFile = new File([compressedFile], safeFileName, {
                type: compressedFile.type,
                lastModified: Date.now()
            });
            
            const photoURL = await uploadPhotoToFirebase(safeFile, currentSelectedOrder.id);
            
            await StorageManager.saveOrder(
                currentSelectedOrder.id,
                { 
                    photos: [...(currentSelectedOrder.photos || []), photoURL],
                    _expectedVersion: currentSelectedOrder.version 
                },
                currentEmployee
            );
            
            alert("Foto wurde sicher hochgeladen!");
        } catch (error) {
            console.error('Upload-Fehler:', error);
            alert('Fehler beim Verarbeiten des Fotos. Bitte versuchen Sie es erneut.');
        }
    };

    const handleMarkAsDone = async () => {
        if (!currentSelectedOrder) return;
        
        const confirmed = await simpleConfirm(
            'Auftrag wirklich als ERLEDIGT markieren? Dies kann nicht rückgängig gemacht werden!'
        );
        
        if (!confirmed) return;
        
        const historyEntry = { status: 'Erledigt', timestamp: new Date().toISOString(), by: currentEmployee };
        
        await StorageManager.saveOrder(
            currentSelectedOrder.id,
            { 
                status: 'Erledigt',
                statusHistory: [...(currentSelectedOrder.statusHistory || []), historyEntry],
                _expectedVersion: currentSelectedOrder.version
            },
            currentEmployee
        );
        
        setViewState('list');
    };

    const openOrderDetails = (order: any) => {
        // Prüfe ob Mitarbeiter berechtigt ist
        if (!order.assignedTo.includes(currentEmployee)) {
            alert('Sie sind diesem Auftrag nicht zugewiesen!');
            return;
        }
        setSelectedOrderId(order.id);
        setViewState('details');
    };

    // ✅ KORRIGIERTE handleSaveDailyHours - Verwende upsertDailyHour!
    const handleSaveDailyHours = async (hours: number, date: string) => {
        console.log(`Mitarbeiter ${currentEmployee} speichert: ${hours}h für ${date}`);
        
        // Nur die Upsert-Methode aufrufen. Der Firebase-Listener kümmert sich um den Rest.
        const success = await StorageManager.upsertDailyHour(currentEmployee, date, hours);
        
        if (!success) {
            alert('Fehler beim Speichern der Arbeitszeiten. Bitte erneut versuchen.');
        }
    };

    // EARLY RETURNS
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl">Lade...</div>
            </div>
        );
    }

    // PIN Login anzeigen wenn nicht authentifiziert
    if (!isAuthenticated) {
        return (
            <PinLogin 
                onSuccess={(user) => {
                    setIsAuthenticated(true);
                    setCurrentEmployee(user);
                    AuthHelper.saveAuth(user); // Speichere für 24h
                }} 
                isChef={false}
                authenticate={authenticateMitarbeiter}
            />
        );
    }

    // Firebase Daten laden
    if (!dataLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Lade Daten...</p>
                </div>
            </div>
        );
    }

    // ========== 7. MAIN RENDER ==========
    if (viewState === 'list') {
        const timeRemaining = AuthHelper.getTimeRemaining();
        const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
        const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

        return (
            <div className="min-h-screen bg-gray-50 safe-bottom">
                {/* Offline-Indikator */}
                {!isOnline && (
                    <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50">
                        ⚠️ Offline-Modus - Änderungen werden lokal gespeichert
                    </div>
                )}

                {/* Header */}
                <div className="bg-white shadow-sm border-b safe-top">
                    <div className="px-4 py-3 flex items-center justify-between">
                        <div>
                            <h1 className="font-bold text-lg text-gray-800">Meine Aufträge</h1>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>Angemeldet als: {currentEmployee}</span>
                                <span className="text-green-600">
                                    (noch {hoursRemaining}h {minutesRemaining}min)
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowCalendarView(true)}
                                className="bg-purple-100 px-3 py-1 rounded-full text-purple-600 hover:bg-purple-200 btn-mobile"
                            >
                                <span className="text-sm">📅 Kalender</span>
                            </button>
                            <button
                                onClick={() => setShowMessagesModal(true)}
                                className="relative bg-gray-100 px-3 py-1 rounded-full text-gray-600 hover:bg-gray-200 btn-mobile"
                            >
                                <span className="text-sm">📨 Nachrichten</span>
                                {unreadCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                            <button 
                                onClick={handleLogout} 
                                className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-600 btn-mobile"
                            >
                                Abmelden
                            </button>
                        </div>
                    </div>
                </div>

                {/* High Priority Message Alert */}
                {unreadMessages.some(msg => msg.priority === 'high') && (
                    <div className="p-3 bg-red-100 border-b border-red-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-red-600 animate-pulse">🔴</span>
                                <span className="text-sm font-medium text-red-800">
                                    Wichtige Nachricht vom Chef!
                                </span>
                            </div>
                            <button
                                onClick={() => setShowMessagesModal(true)}
                                className="text-sm text-red-800 underline btn-mobile"
                            >
                                Anzeigen
                            </button>
                        </div>
                    </div>
                )}

                {/* SCHNELLERFASSUNG FORMULAR */}
                <div className="p-3 bg-blue-50 border-b">
                    <h3 className="font-medium text-gray-800 mb-3">📝 Schnellerfassung</h3>
                    <div className="space-y-2">
                        <input
                            type="text"
                            placeholder="Kunde *"
                            value={quickCreateForm.customerName}
                            onChange={(e) => setQuickCreateForm(prev => ({ ...prev, customerName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <input
                            type="text"
                            placeholder="Fahrzeug (optional)"
                            value={quickCreateForm.vehicle}
                            onChange={(e) => setQuickCreateForm(prev => ({ ...prev, vehicle: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <textarea
                            placeholder="Aufgaben / Notizen..."
                            value={quickCreateForm.tasks}
                            onChange={(e) => setQuickCreateForm(prev => ({ ...prev, tasks: e.target.value }))}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={handleQuickCreateOpen}
                                className="flex-1 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium btn-mobile"
                            >
                                Als offen speichern
                            </button>
                            <button
                                onClick={handleQuickCreateCompleted}
                                className="flex-1 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 font-medium btn-mobile"
                            >
                                ✓ Als erledigt speichern
                            </button>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="p-3 space-y-2">
                    <button 
                        onClick={handleCreateNewOrder} 
                        className="w-full py-3 bg-gray-600 text-white font-semibold rounded-lg shadow-sm hover:bg-gray-700 transition-colors btn-mobile"
                    >
                        + Detaillierten Auftrag erstellen
                    </button>
                    
                    <button 
                        onClick={() => setShowDailyHoursModal(true)}
                        className={`w-full py-3 font-semibold rounded-lg shadow-sm transition-colors relative btn-mobile ${
                            !isWorkday
                                ? missingWorkdays > 0 
                                    ? 'bg-orange-500 text-white hover:bg-orange-600' // Fehlende Arbeitstage trotz WE/Feiertag
                                    : 'bg-gray-200 text-gray-500' // Alles erledigt
                                : isLateReminder 
                                    ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                                    : todayHoursEntered
                                        ? 'bg-gray-300 text-gray-600'
                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        🕐 Tagesstunden eintragen
                        {isWorkday && !todayHoursEntered && (
                            <span className="absolute top-2 right-2 w-2 h-2 bg-red-600 rounded-full"></span>
                        )}
                        {isWorkday && todayHoursEntered && (
                            <span className="text-xs ml-2">✓ Heute erledigt</span>
                        )}
                        {!isWorkday && missingWorkdays > 0 && (
                            <span className="text-xs ml-2">
                                ⚠️ {missingWorkdays} Arbeitstage fehlen
                            </span>
                        )}
                        {!isWorkday && missingWorkdays === 0 && (
                            <span className="text-xs ml-2">
                                {isHoliday ? '(Feiertag)' : '(Wochenende)'}
                            </span>
                        )}
                    </button>
                </div>

                {/* Tabs */}
                <div className="px-3">
                    <div className="flex border-b">
                        <button
                            onClick={() => setActiveTab('active')}
                            className={`flex-1 py-2 text-center font-medium ${
                                activeTab === 'active' 
                                    ? 'border-b-2 border-blue-500 text-blue-600' 
                                    : 'text-gray-500'
                            }`}
                        >
                            Aktive ({activeOrders.filter(o => o.status !== 'draft').length})
                        </button>
                        <button
                            onClick={() => setActiveTab('completed')}
                            className={`flex-1 py-2 text-center font-medium ${
                                activeTab === 'completed' 
                                    ? 'border-b-2 border-blue-500 text-blue-600' 
                                    : 'text-gray-500'
                            }`}
                        >
                            Erledigt ({completedOrders.length})
                        </button>
                    </div>
                </div>

                {/* Modals */}
                {showDailyHoursModal && (
                    <DailyHoursModal
                        employee={currentEmployee}
                        dailyHours={dailyHours}
                        setDailyHours={setDailyHours}
                        onClose={() => setShowDailyHoursModal(false)}
                        onSave={handleSaveDailyHours}
                    />
                )}
                
                {showMessagesModal && (
                    <MessagesModal
                        employee={currentEmployee}
                        messages={myMessages}
                        onClose={() => setShowMessagesModal(false)}
                        onMarkAsRead={async (messageId) => {
                            const updatedMessages = messages.map(msg => 
                                msg.id === messageId ? { ...msg, read: true } : msg
                            );
                            setMessages(updatedMessages);
                            await StorageManager.saveMessages(updatedMessages);
                        }}
                    />
                )}
                
                {showCalendarView && (
                    <CalendarView
                        employee={currentEmployee}
                        dailyHours={dailyHours}
                        onDateClick={(date) => {
                            setShowCalendarView(false);
                            setShowDailyHoursModal(true);
                            // Der DailyHoursModal wird automatisch das richtige Datum auswählen
                        }}
                        onClose={() => setShowCalendarView(false)}
                    />
                )}

                {/* Orders Liste basierend auf Tab */}
                <div className="px-3 pb-3 pt-4 space-y-3">
                    {activeTab === 'active' ? (
                        activeOrders.filter(o => o.status !== 'draft').length > 0 ? (
                            activeOrders
                                .filter(o => o.status !== 'draft')
                                .sort((a, b) => {
                                    // Sortiere nach Priorität
                                    const priorityOrder = { high: 0, medium: 1, low: 2 };
                                    const aPriority = priorityOrder[a.tasksPriority as keyof typeof priorityOrder] ?? 1;
                                    const bPriority = priorityOrder[b.tasksPriority as keyof typeof priorityOrder] ?? 1;
                                    return aPriority - bPriority;
                                })
                                .map(order => {
                                    const getPriorityBadge = (priority: string) => {
                                        switch(priority) {
                                            case 'high': return '🔴';
                                            case 'low': return '🟢';
                                            default: return '🟡';
                                        }
                                    };
                                    
                                    return (
                                        <div 
                                            key={order.id}
                                            onClick={() => openOrderDetails(order)}
                                            className="bg-white rounded-lg p-4 shadow-sm active:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-gray-800 truncate">
                                                            {order.customerName}
                                                        </h3>
                                                        <span className="text-lg">
                                                            {getPriorityBadge(order.tasksPriority || 'medium')}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 truncate">
                                                        {order.vehicle}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                            order.status === 'Neu' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {order.status}
                                                        </span>
                                                        {order.timeEntries?.length > 0 && (
                                                            <span className="text-xs text-gray-500">
                                                                {order.timeEntries.reduce((sum: number, entry: any) => sum + entry.hours, 0).toFixed(1)}h
                                                            </span>
                                                        )}
                                                        {order.photos?.length > 0 && (
                                                            <span className="text-xs text-gray-500">
                                                                📷 {order.photos.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right ml-2">
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <span className="text-gray-400 text-xs">›</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-gray-400 text-xl">📋</span>
                                </div>
                                <p className="text-gray-500">Keine aktiven Aufträge</p>
                                <p className="text-xs text-gray-400 mt-2">Verwende die Schnellerfassung oben!</p>
                            </div>
                        )
                    ) : (
                        completedOrders.length > 0 ? (
                            completedOrders
                                .sort((a, b) => new Date(b.lastModified || b.createdAt).getTime() - new Date(a.lastModified || a.createdAt).getTime())
                                .map(order => (
                                    <div 
                                        key={order.id}
                                        onClick={() => openOrderDetails(order)}
                                        className="bg-white rounded-lg p-4 shadow-sm active:shadow-md transition-shadow opacity-75"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-800 truncate">
                                                    {order.customerName}
                                                </h3>
                                                <p className="text-sm text-gray-600 truncate">
                                                    {order.vehicle}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-green-100 text-green-700">
                                                        ✓ Erledigt
                                                    </span>
                                                    {order.timeEntries?.length > 0 && (
                                                        <span className="text-xs text-gray-500">
                                                            {order.timeEntries.reduce((sum: number, entry: any) => sum + entry.hours, 0).toFixed(1)}h
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-gray-400">
                                                        {new Date(order.lastModified || order.createdAt).toLocaleDateString('de-AT')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right ml-2">
                                                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                                                    <span className="text-gray-400 text-xs">›</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-gray-400 text-xl">✅</span>
                                </div>
                                <p className="text-gray-500">Keine erledigten Aufträge</p>
                            </div>
                        )
                    )}
                </div>
            </div>
        );
    }

    // Order Details
    return (
        <OrderDetails 
            order={currentSelectedOrder}
            orders={orders}
            setOrders={setOrders}
            currentEmployee={currentEmployee}
            onBack={() => setViewState('list')}
            formState={formState}
            setFormState={setFormState}
            handleFormChange={handleFormChange}
            handleAssignmentChange={handleAssignmentChange}
            handleSaveAndExit={handleSaveAndExit}
            handleAddTimeEntry={handleAddTimeEntry}
            handleAddMaterials={handleAddMaterials}
            handleDeleteTimeEntry={handleDeleteTimeEntry}
            handleSaveTimeEntryUpdate={handleSaveTimeEntryUpdate}
            handlePhotoUpload={handlePhotoUpload}
            handleMarkAsDone={handleMarkAsDone}
            editingTimeEntryId={editingTimeEntryId}
            setEditingTimeEntryId={setEditingTimeEntryId}
            editingTimeEntryHours={editingTimeEntryHours}
            setEditingTimeEntryHours={setEditingTimeEntryHours}
            validationErrors={validationErrors}
            isOnline={isOnline}
        />
    );
}

// OrderDetails Komponente mit Prioritätsanzeige
interface OrderDetailsProps {
    order: any;
    orders: any[];
    setOrders: React.Dispatch<React.SetStateAction<any[]>>;
    currentEmployee: string;
    onBack: () => void;
    formState: any;
    setFormState: React.Dispatch<React.SetStateAction<any>>;
    handleFormChange: (field: string, value: any) => void;
    handleAssignmentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSaveAndExit: () => void;
    handleAddTimeEntry: () => void;
    handleAddMaterials: () => void;
    handleDeleteTimeEntry: (entryId: string) => void;
    handleSaveTimeEntryUpdate: (entryId: string) => void;
    handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleMarkAsDone: () => void;
    editingTimeEntryId: string | null;
    setEditingTimeEntryId: React.Dispatch<React.SetStateAction<string | null>>;
    editingTimeEntryHours: number;
    setEditingTimeEntryHours: React.Dispatch<React.SetStateAction<number>>;
    validationErrors: any;
    isOnline: boolean;
}

function OrderDetails({ 
    order, orders, setOrders, currentEmployee, onBack,
    formState, setFormState, handleFormChange, handleAssignmentChange,
    handleSaveAndExit, handleAddTimeEntry, handleAddMaterials,
    handleDeleteTimeEntry, handleSaveTimeEntryUpdate, handlePhotoUpload,
    handleMarkAsDone, editingTimeEntryId, setEditingTimeEntryId,
    editingTimeEntryHours, setEditingTimeEntryHours, validationErrors, isOnline
}: OrderDetailsProps) {
    const [editingTasks, setEditingTasks] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    if (!order) return null;

    // Warnung bei ungespeicherten Änderungen
    const handleBackWithWarning = async () => {
        if (unsavedChanges) {
            if (await window.confirm('Es gibt ungespeicherte Änderungen. Wirklich verlassen?')) {
                onBack();
            }
        } else {
            onBack();
        }
    };

    const handleTaskToggle = async (taskId: string, checked: boolean) => {
        const updatedText = updateTasksText(formState.tasks, taskId, checked);
        setFormState((prev: any) => ({ ...prev, tasks: updatedText }));
        setUnsavedChanges(true);
        
        // Update sofort speichern
        await StorageManager.saveOrder(
            order.id,
            { 
                tasks: updatedText,
                _expectedVersion: order.version 
            },
            currentEmployee
        );
    };

    const handleTasksEdit = () => {
        setEditingTasks(true);
    };

    const handleTasksSave = async () => {
        await StorageManager.saveOrder(
            order.id,
            { 
                tasks: formState.tasks,
                _expectedVersion: order.version
            },
            currentEmployee
        );
        
        setEditingTasks(false);
        setUnsavedChanges(false);
    };

    const tasksData = parseTasksWithCheckboxes(formState.tasks, formState.tasksPriority);

    const handleDetailsMaterialChange = (index: number, field: string, value: string) => {
        const materials = [...formState.newMaterials];
        materials[index] = { ...materials[index], [field]: value };
        setFormState((prev: any) => ({ ...prev, newMaterials: materials }));
        setUnsavedChanges(true);
    };
    
    const addDetailsMaterialRow = () => setFormState((prev: any) => ({ 
        ...prev, 
        newMaterials: [...prev.newMaterials, { quantity: '', name: '' }] 
    }));
    
    const removeDetailsMaterialRow = (index: number) => {
        if (formState.newMaterials.length > 1) {
            setFormState((prev: any) => ({ 
                ...prev, 
                newMaterials: formState.newMaterials.filter((_: any, i: number) => i !== index) 
            }));
        }
    };

    const getPriorityLabel = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴 Wichtig';
            case 'low': return '🟢 Niedrig';
            default: return '🟡 Mittel';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 safe-bottom">
            {/* Offline-Indikator */}
            {!isOnline && (
                <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50">
                    ⚠️ Offline-Modus - Änderungen werden lokal gespeichert
                </div>
            )}

            {/* Header */}
            <div className="bg-white shadow-sm border-b safe-top">
                <div className="px-4 py-3 flex items-center justify-between">
                    <button 
                        onClick={handleBackWithWarning}
                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center btn-mobile"
                    >
                        <span className="text-gray-600">‹</span>
                    </button>
                    <h1 className="font-semibold text-gray-800 truncate px-2">
                        {order.customerName || 'Neuer Auftrag'}
                    </h1>
                    <div className="w-8">
                        {unsavedChanges && (
                            <span className="text-xs text-orange-500">●</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Prioritätsanzeige */}
                <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Priorität:</span>
                        <span className="text-lg font-medium">
                            {getPriorityLabel(formState.tasksPriority || 'medium')}
                        </span>
                    </div>
                </div>

                {/* Order Form */}
                <div className="bg-white rounded-lg p-4">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Kunde <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                value={formState.customerName || ''} 
                                onChange={(e) => {
                                    handleFormChange('customerName', e.target.value);
                                    setUnsavedChanges(true);
                                }} 
                                className={`w-full px-3 py-2 border rounded-md ${
                                    validationErrors.customerName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Kundenname eingeben..."
                            />
                            {validationErrors.customerName && (
                                <p className="text-red-500 text-xs mt-1">{validationErrors.customerName}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fahrzeug
                            </label>
                            <input 
                                type="text" 
                                value={formState.vehicle || ''} 
                                onChange={(e) => {
                                    handleFormChange('vehicle', e.target.value);
                                    setUnsavedChanges(true);
                                }} 
                                className={`w-full px-3 py-2 border rounded-md ${
                                    validationErrors.vehicle ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Fahrzeug eingeben..."
                            />
                            {validationErrors.vehicle && (
                                <p className="text-red-500 text-xs mt-1">{validationErrors.vehicle}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Zuweisen an: <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4 mt-2">
                                {MITARBEITER.map(emp => (
                                    <label key={emp} className="flex items-center gap-2">
                                        <input 
                                            type="checkbox" 
                                            value={emp} 
                                            checked={(formState.assignedTo || []).includes(emp)} 
                                            onChange={(e) => {
                                                handleAssignmentChange(e);
                                                setUnsavedChanges(true);
                                            }} 
                                        />
                                        <span className="text-sm">{emp}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Tasks with Checkboxes */}
                <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-800">Aufgaben</h3>
                        {editingTasks && (
                            <div className="flex gap-2">
                                <button 
                                    onClick={handleTasksSave}
                                    className="text-sm bg-green-500 text-white px-3 py-1 rounded btn-mobile"
                                >
                                    ✓ Speichern
                                </button>
                                <button 
                                    onClick={() => setEditingTasks(false)}
                                    className="text-sm bg-gray-300 text-gray-700 px-3 py-1 rounded btn-mobile"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {editingTasks ? (
                        <div>
                            <textarea 
                                value={formState.tasks || ''} 
                                onChange={(e) => {
                                    handleFormChange('tasks', e.target.value);
                                    setUnsavedChanges(true);
                                }} 
                                rows={6} 
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="Aufgaben eingeben...&#10;&#10;Einfache Checkboxen:&#10;-- Ölwechsel&#10;-- Bremsbeläge prüfen&#10;-- TÜV-Vorbereitung&#10;&#10;Normaler Text ohne --"
                            />
                            <p className="text-xs text-gray-500 mt-2 mb-3">
                                💡 <strong>Super einfach:</strong> Schreibe <code>--</code> für Checkboxen, z.B. "-- Ölwechsel"
                            </p>
                        </div>
                    ) : (
                        <div>
                            <TaskRenderer 
                                tasks={tasksData}
                                originalText={formState.tasks}
                                priority={formState.tasksPriority}
                                onTaskToggle={handleTaskToggle}
                                onEditStart={handleTasksEdit}
                                showEditButton={true}
                            />
                        </div>
                    )}
                </div>

                {/* Time Tracking with Security */}
                <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-800">Arbeitszeiten</h3>
                        <span className="text-sm text-gray-500">
                            Gesamt: {(order.timeEntries || []).reduce((sum: number, e: any) => sum + e.hours, 0).toFixed(1)}h
                        </span>
                    </div>
                    {order.timeEntries?.length > 0 && (
                        <div className="space-y-2 mb-4">
                            {order.timeEntries.map((entry: any) => (
                                <div key={entry.id} className="bg-gray-50 p-2 rounded-md flex items-center justify-between">
                                    {editingTimeEntryId === entry.id ? (
                                        <>
                                            <input 
                                                type="number" 
                                                step="0.1" 
                                                value={editingTimeEntryHours} 
                                                onChange={e => setEditingTimeEntryHours(parseFloat(e.target.value))} 
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                            />
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => handleSaveTimeEntryUpdate(entry.id)} 
                                                    className="text-green-600 text-sm px-2 btn-mobile"
                                                >
                                                    ✓
                                                </button>
                                                <button 
                                                    onClick={() => setEditingTimeEntryId(null)} 
                                                    className="text-red-600 text-sm px-2 btn-mobile"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <p className="font-semibold text-sm">
                                                    {entry.hours} Std. 
                                                    <span className={`text-xs ml-2 ${
                                                        entry.employee === currentEmployee 
                                                            ? 'text-green-600' 
                                                            : 'text-gray-500'
                                                    }`}>
                                                        von {entry.employee}
                                                    </span>
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(entry.timestamp).toLocaleString('de-AT')}
                                                </p>
                                            </div>
                                            {entry.employee === currentEmployee && (
                                                <div className="flex gap-2">
                                                    <button 
                                                        onClick={() => { 
                                                            setEditingTimeEntryId(entry.id); 
                                                            setEditingTimeEntryHours(entry.hours); 
                                                        }} 
                                                        className="text-blue-600 text-xs px-2 btn-mobile"
                                                    >
                                                        Ändern
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteTimeEntry(entry.id)} 
                                                        className="text-red-600 text-xs px-2 btn-mobile"
                                                    >
                                                        Löschen
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input 
                            type="number" 
                            step="0.1" 
                            min="0"
                            max="24"
                            value={formState.hoursToAdd || ''} 
                            onChange={(e) => {
                                handleFormChange('hoursToAdd', e.target.value);
                            }} 
                            placeholder="Stunden" 
                            className={`flex-1 px-3 py-2 border rounded-md text-sm ${
                                validationErrors.hours ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        <button 
                            onClick={handleAddTimeEntry} 
                            className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium btn-mobile"
                        >
                            Hinzufügen
                        </button>
                    </div>
                    {validationErrors.hours && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.hours}</p>
                    )}
                </div>
                
                {/* Materials */}
                <div className="bg-white rounded-lg p-4">
                    <h3 className="font-medium text-gray-800 mb-3">Material</h3>
                    {order.materials?.length > 0 && (
                        <div className="bg-gray-50 p-3 rounded-md text-sm mb-4">
                            <p className="font-bold mb-2">Bisher erfasst:</p>
                            <div className="space-y-1">
                                {order.materials.map((m: any, i: number) => (
                                    <div key={i} className="flex justify-between items-center">
                                        <span>{m.quantity}x {m.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {formState.newMaterials?.map((item: any, index: number) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                            <input 
                                type="text" 
                                placeholder="Menge" 
                                value={item.quantity || ''} 
                                onChange={(e) => handleDetailsMaterialChange(index, 'quantity', e.target.value)} 
                                className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm"
                            />
                            <input 
                                type="text" 
                                placeholder="Artikel" 
                                value={item.name || ''} 
                                onChange={(e) => handleDetailsMaterialChange(index, 'name', e.target.value)} 
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                            <button 
                                onClick={() => removeDetailsMaterialRow(index)} 
                                className="text-red-500 text-sm px-2 btn-mobile"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    <div className="flex gap-2">
                        <button 
                            onClick={addDetailsMaterialRow} 
                            className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm btn-mobile"
                        >
                            + Zeile
                        </button>
                        <button 
                            onClick={handleAddMaterials} 
                            className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium btn-mobile"
                        >
                            Material hinzufügen
                        </button>
                    </div>
                </div>

                {/* Photos */}
                <div className="bg-white rounded-lg p-4">
                    <h3 className="font-medium text-gray-800 mb-3">Fotos</h3>
                    {order.photos?.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {order.photos.map((url: string, i: number) => {
                                // SICHERHEIT: Validiere URL bevor Anzeige
                                const isValidUrl = (urlString: string) => {
                                    try {
                                        const url = new URL(urlString);
                                        // Nur HTTPS URLs von Firebase Storage erlauben
                                        return url.protocol === 'https:' && 
                                               url.hostname.includes('firebasestorage.app');
                                    } catch {
                                        return false;
                                    }
                                };
                                
                                if (!isValidUrl(url)) {
                                    console.error('Unsichere Foto-URL blockiert:', url);
                                    return null;
                                }
                                
                                return (
                                    <div key={i} className="relative">
                                        <img 
                                            src={url} 
                                            alt={`Foto ${i+1}`} 
                                            className="w-full h-20 object-cover rounded-md border cursor-pointer"
                                            onClick={() => window.open(url, '_blank')}
                                            onError={(e) => {
                                                // Fallback bei Ladefehler
                                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik00NSA0MEw0NSA2ME01NSA0MEw1NSA2ME00MCA1MEw2MCA1MCIgc3Ryb2tlPSIjOUI5Q0E0IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4=';
                                                e.currentTarget.alt = 'Bild konnte nicht geladen werden';
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div>
                        <label htmlFor="photo-upload" className="block w-full py-3 bg-blue-500 text-white text-center rounded-md font-medium btn-mobile hover:bg-blue-600">
                            📷 Foto aufnehmen/hochladen
                        </label>
                        <input 
                            id="photo-upload" 
                            type="file" 
                            accept="image/jpeg,image/png,image/webp" 
                            capture="environment" 
                            className="hidden" 
                            onChange={handlePhotoUpload} 
                        />
                        <p className="text-xs text-gray-500 mt-2">Max. 10MB • Nur JPEG, PNG, WebP • Keine GIFs oder SVGs</p>
                    </div>
                </div>
                
                {/* Status History */}
                <div className="bg-white rounded-lg p-4">
                    <h3 className="font-medium text-gray-800 mb-3">Verlauf</h3>
                    <div className="space-y-1 text-xs text-gray-500">
                        {order.statusHistory?.map((entry: any, i: number) => (
                            <p key={i}>
                                <span className="font-bold">{entry.status}</span> am{' '}
                                {new Date(entry.timestamp).toLocaleString('de-AT')} durch {entry.by}
                            </p>
                        ))}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    <button 
                        onClick={handleSaveAndExit} 
                        className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg shadow-md hover:bg-blue-700 btn-mobile"
                    >
                        💾 Speichern & Zurück zur Liste
                    </button>
                    {order.status !== 'Erledigt' && (
                        <button 
                            onClick={handleMarkAsDone} 
                            className="w-full py-3 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600 btn-mobile"
                        >
                            ✅ Auftrag als ERLEDIGT markieren
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ERWEITERTE MessagesModal mit Prioritäten
interface MessagesModalProps {
    employee: string;
    messages: any[];
    onClose: () => void;
    onMarkAsRead: (messageId: string) => void;
}

function MessagesModal({ employee, messages, onClose, onMarkAsRead }: MessagesModalProps) {
    useEffect(() => {
        // Mark all unread messages as read when modal opens
        messages.forEach(msg => {
            if (!msg.read) {
                setTimeout(() => onMarkAsRead(msg.id), 1000);
            }
        });
    }, [messages, onMarkAsRead]);
    
    const sortedMessages = [...messages].sort((a, b) => {
        // Sortiere nach Priorität und dann nach Datum
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 1;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 1;
        
        if (aPriority !== bPriority) {
            return aPriority - bPriority;
        }
        
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    
    const getPriorityIcon = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴';
            case 'low': return '🟢';
            default: return '🟡';
        }
    };
    
    const getPriorityLabel = (priority: string) => {
        switch(priority) {
            case 'high': return 'Hoch';
            case 'low': return 'Niedrig';
            default: return 'Mittel';
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Nachrichten</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 btn-mobile">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                    {sortedMessages.length > 0 ? (
                        <div className="divide-y">
                            {sortedMessages.map(msg => (
                                <div 
                                    key={msg.id} 
                                    className={`p-4 ${
                                        !msg.read ? 'bg-blue-50' : ''
                                    } ${
                                        msg.priority === 'high' ? 'border-l-4 border-red-500' : ''
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-800">
                                                {msg.from}
                                            </span>
                                            <span className="text-xs">
                                                {getPriorityIcon(msg.priority || 'medium')}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded ${
                                                msg.priority === 'high' 
                                                    ? 'bg-red-100 text-red-700' 
                                                    : msg.priority === 'low'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {getPriorityLabel(msg.priority || 'medium')}
                                            </span>
                                            {!msg.read && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                                    Neu
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {new Date(msg.createdAt).toLocaleString('de-DE', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                day: '2-digit',
                                                month: '2-digit'
                                            })}
                                        </span>
                                    </div>
                                    <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                    {msg.to.length > 1 && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            An: {msg.to.join(', ')}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <span className="text-3xl">📭</span>
                            <p className="mt-2">Keine Nachrichten</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// DailyHoursModal Komponente
interface DailyHoursModalProps {
    employee: string;
    dailyHours: any[];
    setDailyHours: React.Dispatch<React.SetStateAction<any[]>>;
    onClose: () => void;
    onSave: (hours: number, date: string) => void;
}

function DailyHoursModal({ employee, dailyHours, setDailyHours, onClose, onSave }: DailyHoursModalProps) {
    const [hours, setHours] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Get hours for the selected date
    const existingEntry = dailyHours.find(entry => 
        entry.employee === employee && entry.date === selectedDate
    );
    
    useEffect(() => {
        if (existingEntry) {
            setHours(existingEntry.hours.toString());
        } else {
            setHours('');
        }
    }, [selectedDate, existingEntry]);
    
    const handleSave = () => {
        const hoursFloat = parseFloat(hours);
        if (isNaN(hoursFloat) || hoursFloat < 0 || hoursFloat > 24) {
            alert('Bitte gültige Stundenzahl eingeben (0-24)');
            return;
        }
        
        onSave(hoursFloat, selectedDate);
        onClose();
    };
    
    // Aktueller Monat
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sonntag
    
    // Feiertage für den aktuellen Monat
    const holidays = getAustrianHolidays(currentYear);
    const today = formatDate(new Date());

    // Kalender-Array aufbauen
    const calendarDays = [];
    // Leere Felder für Tage vor Monatsbeginn (Montag = 1, Sonntag = 0)
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    for (let i = 0; i < adjustedStart; i++) {
        calendarDays.push(null);
    }
    // Tage des Monats
    for (let day = 1; day <= lastDay.getDate(); day++) {
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const entry = dailyHours.find(e => e.employee === employee && e.date === dateStr);
        const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
        calendarDays.push({
            date: dateStr,
            day: day,
            hours: entry ? entry.hours : null, // Zeige auch 0 Stunden an
            isWeekend: dayOfWeek % 6 === 0,
            isHoliday: !!holidays[dateStr],
            holidayName: holidays[dateStr] || null,
            isToday: dateStr === today,
            isPast: new Date(dateStr) < new Date(today)
        });
    }
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Tagesstunden eintragen</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 btn-mobile">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Datum
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Arbeitsstunden
                            </label>
                            <input
                                type="number"
                                step="0.5"
                                min="0"
                                max="24"
                                value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                placeholder="z.B. 8.5"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg"
                                autoFocus
                            />
                        </div>
                        
                        <button
                            onClick={handleSave}
                            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 btn-mobile"
                        >
                            {existingEntry ? 'Aktualisieren' : 'Speichern'}
                        </button>
                    </div>
                    
                    <div className="mt-6">
                        <h3 className="font-medium text-gray-800 mb-3">
                            {new Date(currentYear, currentMonth).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                        </h3>
                        <div className="grid grid-cols-7 gap-1">
                            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                                <div key={day} className="text-center text-xs font-medium text-gray-500 p-1">
                                    {day}
                                </div>
                            ))}
                            {calendarDays.map((day, index) => 
                                day === null ? (
                                    <div key={`empty-${index}`} />
                                ) : (
                                    <div
                                        key={day.date}
                                        onClick={() => setSelectedDate(day.date)}
                                        className={`
                                            p-2 text-center rounded text-xs btn-mobile cursor-pointer
                                            ${day.date === selectedDate ? 'bg-blue-500 text-white' : ''}
                                            ${day.hours !== null && day.hours !== undefined && day.date !== selectedDate ? 'bg-green-100' : ''}
                                            ${day.isHoliday && day.date !== selectedDate ? 'bg-orange-100' : ''}
                                            ${day.isWeekend && (day.hours === null || day.hours === undefined) && !day.isHoliday && day.date !== selectedDate ? 'bg-gray-100' : ''}
                                            ${(day.hours === null || day.hours === undefined) && !day.isWeekend && !day.isHoliday && day.date !== selectedDate && day.isPast ? 'bg-red-50' : ''}
                                            ${(day.hours === null || day.hours === undefined) && !day.isWeekend && !day.isHoliday && day.date !== selectedDate && !day.isPast ? 'bg-white' : ''}
                                        `}
                                        title={day.holidayName || ''}
                                    >
                                        <div className="font-medium">{day.day}</div>
                                        {day.hours !== null && day.hours !== undefined && (
                                            <div className="text-xs">{day.hours}h</div>
                                        )}
                                        {day.isHoliday && (
                                            <div className="text-xs text-orange-700 font-bold">Feiertag</div>
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                        
                        <div className="mt-3 text-xs text-gray-500">
                            <div className="flex items-center gap-4 flex-wrap">
                                <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-green-100 rounded"></span> Eingetragen
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-red-50 rounded"></span> Fehlt
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-orange-100 rounded"></span> Feiertag
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-gray-100 rounded"></span> Wochenende
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}